import Badge from '@atlaskit/badge';
import Checkbox from '@atlaskit/checkbox';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import Textfield from '@atlaskit/textfield';

import React, { useEffect, useState, FC, useCallback, Dispatch, SetStateAction } from 'react';
import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled, WebTarget } from 'styled-components';

import { Dimensions } from 'components/visuals';
import { OverviewEntity, OverviewWithOrgChildren } from '../duck/models/accountId';
import { SearchTooltip } from './licensesExpandedRow/Tooltips/Search';
import {
  SearchInput,
  StyledCheckboxWrapper,
  StyledSearchBarWrapper,
} from '../components/styledComponents';
import { CustomerAccountOverviewSearchDTO } from '../duck/models';
import { messages } from '../messages';
import { useDebounce } from '../common/hooks';
import { generateMonthList, removeTimezone } from '../common/utils';
import t from 'modules/i18n/intl';
import { ExpiringDateSelectOption } from '../common/types';
import Select from '@atlaskit/select';
import { selectCustomerAccountOverviews } from '../duck/selectors';

export type SearchLineProps = {
  allLicenses: Array<OverviewEntity | OverviewWithOrgChildren>;
  updateLicensesToDisplay:
    | Dispatch<SetStateAction<OverviewEntity[]>>
    | Dispatch<SetStateAction<OverviewWithOrgChildren[]>>;
  updateSearchParams: (
    searchObject:
      | CustomerAccountOverviewSearchDTO
      | ((prevVar: CustomerAccountOverviewSearchDTO) => CustomerAccountOverviewSearchDTO),
  ) => void;
  searchParams?: CustomerAccountOverviewSearchDTO;
  isPartner?: boolean;
  partnerId?: number;
  intl: InjectedIntl;
  senNumber?: string;
  preselectedLicenseBySen?: OverviewEntity;
};

export type LicensesByType = {
  archived: Array<OverviewEntity>;
  trial: Array<OverviewEntity>;
  active: Array<OverviewEntity>;
};

const StyledSelect = styled(Select)`
  max-width: ${Dimensions.units(28)}px;
  width: 100%;
`;

const BadgeWrapper = styled.span`
  margin-left: ${Dimensions.units(0.5)}px;
`;

const StyledDiv = styled.div`
  width: ${Dimensions.units(28)}px;
`;

const checkFieldIncludesInput = (searchInput: string, field: string) => {
  let found = true;
  const searchInputQueryList = searchInput.split(' ').filter((s) => s);
  searchInputQueryList.forEach((query) => {
    if (!field.toString().toLowerCase().includes(query.toLowerCase())) {
      found = false;
      return false;
    }
    return true;
  });
  return !searchInput || found;
};

const sortLicenses = (licenses: Array<OverviewEntity>) => {
  const licensesCopy = licenses.slice();
  return licensesCopy.sort(
    (firstLicenseItem, secondLicenseItem) =>
      new Date(secondLicenseItem.expireDate).getTime() -
      new Date(firstLicenseItem.expireDate).getTime(),
  );
};

const SearchLicensesLine: FC<SearchLineProps> = ({
  allLicenses,
  updateLicensesToDisplay,
  updateSearchParams,
  searchParams,
  isPartner,
  partnerId,
  senNumber,
  preselectedLicenseBySen,
  intl,
}) => {
  const [activeChecked, setActiveChecked] = useState<boolean>();
  const [trialChecked, setTrialChecked] = useState<boolean>();
  const [archivedChecked, setArchivedChecked] = useState<boolean>();
  const [activeNumber, setActiveNumber] = useState<number>();
  const [trialNumber, setTrialNumber] = useState<number>();
  const [archivedNumber, setArchivedNumber] = useState<number>();
  const [searchInput, setSearchInput] = useState<string>('');
  const [licensesByType, setLicensesByType] = useState<LicensesByType>({
    archived: [],
    trial: [],
    active: [],
  });
  const debouncedSearchInput = useDebounce(searchInput, 400);
  const licensesByExpireDateSearchResult = useSelector(selectCustomerAccountOverviews);

  const setDefaultCheckBoxValues = () => {
    setActiveChecked(true);
    setTrialChecked(false);
    setArchivedChecked(false);
  };

  const filterLicensesBySearchInput = (
    licenses: Array<OverviewEntity>,
    searchInputString: string,
  ) =>
    licenses.filter(
      ({
        accountId,
        licenseId,
        productDescription,
        mainContactOrganisationName,
        mainContactEmail,
        accountName,
        friendlyName,
        expireDate,
      }) => {
        const fieldsToCheck = [
          accountId ? `sen-${accountId}` : `sen-l${licenseId}`,
          productDescription,
          friendlyName || accountName,
          intl.formatDate(removeTimezone(expireDate), {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          }),
        ];
        if (isPartner) {
          fieldsToCheck.push(mainContactEmail, mainContactOrganisationName);
        }
        return checkFieldIncludesInput(searchInputString, fieldsToCheck.join(' '));
      },
    );

  const onSearchSubmit = useCallback(() => {
    if (isPartner && partnerId) {
      const searchInputLower = debouncedSearchInput.toLocaleLowerCase();
      const searchObject: CustomerAccountOverviewSearchDTO = {
        partnerId,
        expireDate: searchParams?.expireDate,
      };

      if (/^(sen)?-?l?\d+$/.test(searchInputLower)) {
        const searchValue = searchInputLower.replace(/^(sen)?-?l?/, '');
        searchObject.accountId = searchValue;
        searchObject.searchByAnyMatchedField = false;
      } else {
        searchObject.accountId = searchInputLower;
        searchObject.accountName = searchInputLower;
        searchObject.mainContactEmail = searchInputLower;
        searchObject.productPackageDescription = searchInputLower;
        searchObject.searchByAnyMatchedField = true;
      }
      updateSearchParams(searchObject);
    }
  }, [debouncedSearchInput, isPartner, partnerId, searchParams?.expireDate]);

  const onExpiringSelect = useCallback(
    (option: ExpiringDateSelectOption) => {
      if (partnerId) {
        const optionDate = intl.formatDate(option.value ?? new Date(), {
          year: 'numeric',
          month: '2-digit',
        });
        const [month, year] = optionDate.split('/');
        const expireDate = option.value ? `${year}-${month}` : undefined;

        updateSearchParams({
          ...(searchParams as CustomerAccountOverviewSearchDTO),
          expireDate,
        });
      }
    },
    [partnerId, searchParams],
  );

  useEffect(() => {
    const initialLicensesByType: LicensesByType = {
      archived: [],
      trial: [],
      active: [],
    };

    const reducedLicensesByType = allLicenses.reduce((acc, currentLicense) => {
      if (currentLicense.archived) {
        return {
          ...acc,
          archived: [...acc.archived, currentLicense],
        };
      }
      if (currentLicense.licenseType === 'EVALUATION') {
        return {
          ...acc,
          trial: [...acc.trial, currentLicense],
        };
      }
      return {
        ...acc,
        active: [...acc.active, currentLicense],
      };
    }, initialLicensesByType);
    setLicensesByType(reducedLicensesByType);
  }, [allLicenses, allLicenses.length]);

  useEffect(() => {
    if (preselectedLicenseBySen?.archived) {
      setArchivedChecked(true);
    } else if (preselectedLicenseBySen?.licenseType === 'EVALUATION') {
      setTrialChecked(true);
    }
  }, [senNumber, preselectedLicenseBySen]);

  useEffect(() => {
    if (!searchInput || searchInput.length === 0) {
      setDefaultCheckBoxValues();
      if (isPartner && partnerId) {
        const searchObject: CustomerAccountOverviewSearchDTO = {
          partnerId,
          expireDate: searchParams?.expireDate,
          searchByAnyMatchedField: false,
        };
        updateSearchParams(searchObject);
      }
    }
  }, [searchInput, searchInput.length, isPartner, partnerId]);

  useEffect(() => {
    const filteredLicenses: LicensesByType = {
      archived: isPartner
        ? licensesByType.archived
        : filterLicensesBySearchInput(licensesByType.archived, debouncedSearchInput),
      trial: isPartner
        ? licensesByType.trial
        : filterLicensesBySearchInput(licensesByType.trial, debouncedSearchInput),
      active: isPartner
        ? licensesByType.active
        : filterLicensesBySearchInput(licensesByType.active, debouncedSearchInput),
    };

    setActiveNumber(filteredLicenses?.active?.length);
    setArchivedNumber(filteredLicenses?.archived?.length);
    setTrialNumber(filteredLicenses?.trial?.length);

    const checkedLicenses = [
      ...(activeChecked ? filteredLicenses.active : []),
      ...(archivedChecked ? filteredLicenses.archived : []),
      ...(trialChecked ? filteredLicenses.trial : []),
    ];

    const sortedLicenses = sortLicenses(checkedLicenses);
    updateLicensesToDisplay(sortedLicenses);
  }, [
    licensesByType,
    activeChecked,
    archivedChecked,
    trialChecked,
    debouncedSearchInput,
    isPartner,
  ]);

  useEffect(() => {
    if (searchParams?.expireDate) updateLicensesToDisplay(licensesByExpireDateSearchResult);
  }, [licensesByExpireDateSearchResult, searchParams?.expireDate]);

  return (
    <StyledSearchBarWrapper>
      <SearchInput>
        <Textfield
          data-cy="search-input"
          value={searchInput}
          onChange={(e) => setSearchInput(e?.currentTarget?.value)}
          placeholder={
            isPartner
              ? 'Filter by SEN, product, organization, or email address'
              : 'Filter by SEN, product, or name'
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearchSubmit();
            }
          }}
          isCompact
        />
      </SearchInput>
      {isPartner && <SearchTooltip />}
      <StyledCheckboxWrapper>
        <Checkbox
          data-cy="checkbox-active"
          isChecked={activeChecked}
          onChange={() => {
            setActiveChecked(!activeChecked);
          }}
          label={
            <>
              <FormattedMessage {...messages.filteringLicenses} />
              {!!activeNumber && (
                <BadgeWrapper>
                  <Badge max={false}>{activeNumber}</Badge>
                </BadgeWrapper>
              )}
            </>
          }
          name="licenses-filter"
          isDisabled={!activeNumber}
        />
        <Checkbox
          data-cy="checkbox-trial"
          isChecked={trialChecked}
          onChange={(event) => setTrialChecked(event.target.checked)}
          label={
            <>
              <EmojiFrequentIcon label="" />
              <FormattedMessage {...messages.filteringLicensesTrial} />
              {!!trialNumber && (
                <BadgeWrapper>
                  <Badge max={false}>{trialNumber}</Badge>
                </BadgeWrapper>
              )}
            </>
          }
          name="trial-licenses-filter"
          isDisabled={!trialNumber}
        />
        <Checkbox
          data-cy="checkbox-archived"
          isChecked={archivedChecked}
          onChange={() => {
            setArchivedChecked(!archivedChecked);
          }}
          label={
            <>
              <FormattedMessage {...messages.filteringLicensesArchived} />
              {!!archivedNumber && (
                <BadgeWrapper>
                  <Badge max={false}>{archivedNumber}</Badge>
                </BadgeWrapper>
              )}
            </>
          }
          name="archived-licenses-filter"
          isDisabled={!archivedNumber}
        />
      </StyledCheckboxWrapper>
      {isPartner && (
        <StyledDiv data-cy="expiring-select">
          <StyledSelect<WebTarget>
            onChange={onExpiringSelect}
            options={generateMonthList()}
            placeholder={t('mac.licenses.expiring.in')}
          />
        </StyledDiv>
      )}
    </StyledSearchBarWrapper>
  );
};

export default injectIntl(SearchLicensesLine);
